import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    ColumnLayout,
    Select,
    Textarea,
    Box,
    StatusIndicator,
    Popover,
    TagEditor,
} from '@amzn/awsui-components-console';
import copy from 'copy-to-clipboard';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import * as Constants from '../constants';
import * as GlobalConstants from '../../../lib/constants';
import { fetchGroups } from '../../../lab-management/group/apis';
import SelectGroup from './SelectGroup';
import uuid from 'uuid';
import { createAsset } from './apis';
import { getCurrentAuthUserAlias, getCurrentAuthUserEmail, isValidInput, LoadingSpinner } from '../../../lib/utils';
import { RegionContext } from '../../../CustomContext';
import { useLabAuth } from '../../../contexts/LabAuthContext';
import LabPermissionDenied from '../../../auth/LabPermissionDenied';

/**
 * Create asset component
 */
export default function CreateAsset() {
    const { isLabAdmin, isLoading } = useLabAuth();
    const history = useHistory();
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [id, setID] = React.useState('');
    const [serial, setSerial] = React.useState('');
    const [name, setName] = React.useState('');
    const [model, setModel] = React.useState('');
    const [categorySelectedOption, setCategorySelectedOption] = React.useState({});
    const [assignee, setAssignee] = React.useState('');
    const [location, setLocation] = React.useState('');
    const [groupSelectedOption, setGroupSelectedOption] = React.useState({});
    const [comment, setComment] = React.useState('');
    const [tags, setTags] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);

    /**
     * display error message
     * @param {*} header
     * @param {*} content
     */
    const displayErrorContent = (header, content) => {
        setErrorMessage([
            {
                header: header,
                type: 'error',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setErrorMessage([]),
                content: <>{content}</>,
                action: (
                    <Button href={GlobalConstants.CONTACT_US} iconAlign="right" target="_blank">
                        Contact us
                    </Button>
                ),
            },
        ]);
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        let success = false;
        setLoading(true);
        setErrorMessage([]);
        try {
            const requester = await getCurrentAuthUserEmail();
            const customTags = [];
            tags.forEach((tag) => {
                const customTag = {};
                if (!isValidInput(tag.key)) {
                    var error = new Error('Bad Request');
                    error.response = { status: 400, data: 'Tag key cannot be empty' };
                    throw error;
                }
                customTag[tag.key] = tag.value;
                customTags.push(customTag);
            });
            await createAsset({
                id: id,
                serial: serial,
                name: name,
                model: model,
                category: categorySelectedOption.label || '',
                assignee: assignee,
                location: location,
                region: selectedRegion.value,
                groupId: groupSelectedOption.description || '',
                groupName: groupSelectedOption.label || '',
                comment: comment,
                creationDate: (Date.now() / 1000).toString(),
                tags: customTags,
                requester: requester,
            });
            success = true;
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status == 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errorMessage = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMessage);
            } else {
                displayErrorContent(
                    'Internal Server Error',
                    'Cannot create an asset, try again later or contact our dev team @ds2-device-farm-dev for more details.'
                );
            }
        }
        setLoading(false);
        if (success) {
            history.push('/office-lab/assets/');
        }
    };

    /**
     * fetch all available groups
     */
    const fetchAllGroups = async () => {
        try {
            const response = await fetchGroups(selectedRegion.value);
            const groups = response['response'];
            return groups;
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        // initialize the component
        if (isLabAdmin) {
            async function setAlias() {
                const alias = await getCurrentAuthUserAlias();
                setAssignee(alias);
            }
            setAlias();
            const assetUUID = uuid.v4();
            setID(assetUUID);
        }
    }, [isLabAdmin]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isLabAdmin) {
        return <LabPermissionDenied />;
    }

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'Asset management', href: '#/office-lab/assets' },
                    { text: 'Create', href: '#/office-lab/assets/create' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" href="#/office-lab/assets">
                                Back
                            </Button>
                            <Button formAction="none" loading={loading} onClick={onClick} variant="primary">
                                Confirm
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Container>
                            <SpaceBetween direction="vertical" size="m">
                                <ColumnLayout columns={1}>
                                    <FormField label="UUID">
                                        <SpaceBetween direction="horizontal" size="xxs">
                                            <Box variant="awsui-key-label">{id}</Box>
                                            <Box display="inline-block">
                                                <Popover
                                                    size="small"
                                                    position="top"
                                                    triggerType="custom"
                                                    dismissButton={false}
                                                    content={<StatusIndicator type="success">copied</StatusIndicator>}
                                                >
                                                    <Button
                                                        variant="inline-icon"
                                                        iconName="copy"
                                                        onClick={() => copy(id)}
                                                    />
                                                </Popover>
                                            </Box>
                                        </SpaceBetween>
                                    </FormField>
                                    <FormField
                                        label={
                                            <span>
                                                Serial number <i>- optional</i>{' '}
                                            </span>
                                        }
                                    >
                                        <Input
                                            placeholder="Enter a serial number if exist"
                                            value={serial}
                                            onChange={(event) => {
                                                setSerial(event.detail.value);
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Asset name">
                                        <Input
                                            placeholder="Enter an asset name"
                                            value={name}
                                            onChange={(event) => {
                                                setName(event.detail.value);
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Model">
                                        <Input
                                            placeholder="Enter an model"
                                            value={model}
                                            onChange={(event) => {
                                                setModel(event.detail.value);
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Category">
                                        <Select
                                            placeholder="Select a category"
                                            selectedOption={categorySelectedOption}
                                            onChange={({ detail }) => setCategorySelectedOption(detail.selectedOption)}
                                            options={Constants.CATEGORY_OPTIONS}
                                            filteringType="auto"
                                        />
                                    </FormField>
                                    <FormField label="Assignee">
                                        <Input
                                            placeholder="Enter an assignee"
                                            value={assignee}
                                            onChange={(event) => {
                                                setAssignee(event.detail.value);
                                            }}
                                        />
                                    </FormField>
                                    <FormField
                                        label={
                                            <span>
                                                Location <i>- optional</i>{' '}
                                            </span>
                                        }
                                    >
                                        <Input
                                            placeholder="Enter a location"
                                            value={location}
                                            onChange={(event) => {
                                                setLocation(event.detail.value);
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Region">
                                        <Input value={selectedRegion.value} disabled />
                                    </FormField>
                                    <FormField
                                        label={
                                            <span>
                                                Group <i>- optional</i>{' '}
                                            </span>
                                        }
                                    >
                                        <SelectGroup
                                            selectedOption={groupSelectedOption}
                                            onChange={({ detail }) => setGroupSelectedOption(detail.selectedOption)}
                                            placeholder="Select a group"
                                            loadingText="Loading groups"
                                            errorText="Error fetching groups"
                                            fetchOptions={fetchAllGroups}
                                        />
                                    </FormField>
                                    <FormField
                                        label={
                                            <span>
                                                Comment <i>- optional</i>{' '}
                                            </span>
                                        }
                                    >
                                        <Textarea
                                            onChange={({ detail }) => setComment(detail.value)}
                                            value={comment}
                                            placeholder="This is a placeholder"
                                        />
                                    </FormField>
                                    <FormField
                                        label={
                                            <span>
                                                Tags <i>- optional</i>{' '}
                                            </span>
                                        }
                                        description="Tags are metadata that you can assign to ADF assets. Each tag consists of a key and an optional value."
                                    >
                                        <TagEditor
                                            i18nStrings={{
                                                tagLimit: (availableTags, tagLimit) =>
                                                    availableTags === tagLimit
                                                        ? 'You can add up to ' + tagLimit + ' tags.'
                                                        : availableTags === 1
                                                        ? 'You can add up to 1 more tag.'
                                                        : 'You can add up to ' + availableTags + ' more tags.',
                                                keyHeader: 'Key',
                                                valueHeader: 'Value',
                                                keyPlaceholder: 'Enter key',
                                                valuePlaceholder: 'Enter value',
                                                addButton: 'Add new tag',
                                                removeButton: 'Remove',
                                                enteredKeyLabel: (key) => 'Use "' + key + '"',
                                                enteredValueLabel: (value) => 'Use "' + value + '"',
                                            }}
                                            tags={tags}
                                            onChange={({ detail }) => setTags(detail.tags)}
                                        />
                                    </FormField>
                                </ColumnLayout>
                            </SpaceBetween>
                        </Container>
                        {errorMessage.length !== 0 && <Flashbar items={errorMessage} />}
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
